/**
 * LeftRail — in-page sidebar (Pinned + Your Content + Search + New-Board CTA).
 */
import React, { useState, useMemo } from 'react';
import {
  Box, VStack, HStack, Text, Input, Textarea, InputGroup, InputLeftElement, Divider,
  IconButton, Tooltip, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalCloseButton, ModalBody, ModalFooter, Button, FormControl, FormLabel, FormHelperText,
  Select, useDisclosure, useToast,
} from '@chakra-ui/react';
import { FiSearch, FiGrid, FiPlus } from 'react-icons/fi';
import { boardsApi } from '../api';
import { EmojiPickerField } from './EmojiPicker';

function Item({ board, active, onClick }) {
  return (
    <HStack onClick={onClick} role="button" px={3} py={2} spacing={2} borderRadius="md"
      bg={active ? 'var(--brand-tint)' : 'transparent'}
      color={active ? 'var(--brand-primary)' : 'gray.700'}
      _hover={{ bg: active ? 'var(--brand-tint)' : 'gray.50' }} cursor="pointer">
      <Text fontSize="md" w="20px" textAlign="center">{board.emoji || '📊'}</Text>
      <Text fontSize="sm" noOfLines={1} fontWeight={active ? 'semibold' : 'normal'}>{board.name}</Text>
    </HStack>
  );
}

function Section({ title, boards, activeId, onPick }) {
  if (!boards || boards.length === 0) return null;
  return (
    <Box mb={4}>
      <Text fontSize="2xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider" px={3} mb={1}>
        {title}
      </Text>
      <VStack align="stretch" spacing={0.5}>
        {boards.map((b) => <Item key={b._id || b.id} board={b} active={(b._id || b.id) === activeId} onClick={() => onPick(b)} />)}
      </VStack>
    </Box>
  );
}

const DESCRIPTION_MAX = 500;

function NewBoardModal({ isOpen, onClose, onCreated }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('📊');
  const [scope, setScope] = useState('personal');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setName(''); setEmoji('📊'); setScope('personal'); setDescription('');
  };

  const save = async () => {
    if (!name.trim()) return toast({ status: 'warning', title: 'Board name required' });
    if (description.length > DESCRIPTION_MAX) {
      return toast({ status: 'warning', title: `Description too long (max ${DESCRIPTION_MAX} chars)` });
    }
    setBusy(true);
    try {
      const r = await boardsApi.createBoard({
        name: name.trim(), emoji, scope, description: description.trim(),
      });
      toast({ status: 'success', title: 'Board created', duration: 2000 });
      reset();
      onCreated && onCreated(r?.data);
      onClose();
    } catch (e) {
      toast({ status: 'error', title: 'Create failed', description: e?.response?.data?.error?.message || e.message });
    } finally { setBusy(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">New board</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Name</FormLabel>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. My Daily Dashboard" autoFocus />
            </FormControl>
            <HStack>
              <FormControl maxW="80px">
                <FormLabel fontSize="sm">Emoji</FormLabel>
                <EmojiPickerField value={emoji} onChange={setEmoji} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Scope</FormLabel>
                <Select value={scope} onChange={(e) => setScope(e.target.value)}>
                  <option value="personal">Personal (only you)</option>
                  <option value="team">Team (everyone in tenant)</option>
                </Select>
              </FormControl>
            </HStack>
            <FormControl>
              <FormLabel fontSize="sm">Description</FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What is this board for? Which questions does it answer? Who's the audience?"
                rows={3}
                resize="vertical"
                maxLength={DESCRIPTION_MAX}
              />
              <FormHelperText fontSize="xs" textAlign="right">
                {description.length} / {DESCRIPTION_MAX}
              </FormHelperText>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={2} size="sm" onClick={onClose}>Cancel</Button>
          <Button bg="var(--brand-primary)" size="sm" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }} _active={{ bg: 'var(--brand-primary-active)' }} isLoading={busy} onClick={save}>Create</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function LeftRail({ boards, currentUserId, activeId, onPick, onBoardCreated, createPermission = true }) {
  const [q, setQ] = useState('');
  const newBoard = useDisclosure();

  const { team, personal } = useMemo(() => {
    const filtered = (boards || []).filter((b) => !q || b.name.toLowerCase().includes(q.toLowerCase()));
    return {
      team:     filtered.filter((b) => b.scope === 'team'),
      personal: filtered.filter((b) => b.scope === 'personal'),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boards, q, currentUserId]);

  return (
    <Box w="280px" h="100%" bg="white" borderRight="1px" borderColor="gray.200" py={4} px={2} overflowY="auto">
      <HStack px={3} mb={3} spacing={2} justify="space-between">
        <HStack spacing={2}>
          <Box color="var(--brand-primary)"><FiGrid /></Box>
          <Text fontWeight="bold" fontSize="md">Insights</Text>
        </HStack>
        {createPermission && (
        <Tooltip label="New board" placement="right">
          <IconButton size="xs" icon={<FiPlus />} aria-label="New board"
            bg="var(--brand-primary)" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }} onClick={newBoard.onOpen} />
        </Tooltip>
        )}
      </HStack>
      <Box px={3} mb={4}>
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none"><FiSearch color="#a0aec0" /></InputLeftElement>
          <Input placeholder="Search boards…" value={q} onChange={(e) => setQ(e.target.value)} borderRadius="md" />
        </InputGroup>
      </Box>
      <Divider mb={3} />
      <Section title="📌 Pinned (Team)" boards={team} activeId={activeId} onPick={onPick} />
      <Section title="🗂 Your Content" boards={personal} activeId={activeId} onPick={onPick} />
      {team.length === 0 && personal.length === 0 && (
        <Text px={3} fontSize="sm" color="gray.500">No boards match "{q}".</Text>
      )}
      <NewBoardModal isOpen={newBoard.isOpen} onClose={newBoard.onClose} onCreated={onBoardCreated} />
    </Box>
  );
}
